import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import { useEffect, useMemo, useState } from "react";
import type { DtoHome } from "@porchpass/api-client";
import { useResponseRedirect, useSectionProgress } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromHomeDownPayment,
  toUpdateHomeDownPaymentReq,
  type HomeDownPaymentFormData,
} from "src/api/adapters";
import {
  safeNumber,
  notEmpty,
  test,
  validate,
  type ValidationMapperFunc,
  greaterThanMinAmount,
  lessThanMaxAmount,
  lessThanMaxLength,
} from "src/utils";
import {
  DollarSquareIcon,
  FormTable,
  FormTableBody,
  HomeFields,
  shouldClearOtherField,
} from "@porchpass/ui";
import {
  fromHomeDownPaymentFormData,
  homeDownPaymentErrorsAfterSubmit,
  toHomeDownPaymentFormData,
} from "src/forms/adapters/home";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import { testOtherTextRequired } from "src/forms/validation/conditionalOther";
import {
  DLA_DOWN_PAYMENT_SOURCE_GIFT,
  DLA_DOWN_PAYMENT_SOURCE_OTHER,
} from "src/forms/validation/conditionalOtherConfigs";

const getUpdatedValidationMapper = ({
  includeSiteImprovement = false,
  includeUpgrades = false,
}): ValidationMapperFunc<HomeDownPaymentFormData> => {
  return (formData) => {
    const downPaymentCtx = {
      downPaymentAmount: safeNumber(formData.down_payment_amount),
    };

    return {
      price: [
        test(notEmpty, "Must enter a value"),
        test(greaterThanMinAmount(0), "Must be greater than $0.00"),
        test(lessThanMaxAmount(100_000_000), "Must be less than $100M"),
      ],
      down_payment_amount: [
        test(notEmpty, "Must enter a value"),
        test(
          (val) => Boolean(safeNumber(val) >= 0),
          "Must be a positive number",
        ),
        test(
          lessThanMaxAmount(safeNumber(formData.price)),
          "Must be less than Home Price",
        ),
      ],
      down_payment_source_type:
        "down_payment_amount" in formData &&
        safeNumber(formData.down_payment_amount) > 0
          ? [test(notEmpty, "Must select a Down Payment Source")]
          : [],
      down_payment_source_other: [
        testOtherTextRequired(
          formData.down_payment_source_type,
          DLA_DOWN_PAYMENT_SOURCE_OTHER,
          downPaymentCtx,
          "Must explain down payment source",
        ),
        test(lessThanMaxLength(200), "Must not exceed 200 characters"),
      ],
      down_payment_giftor_name: [
        testOtherTextRequired(
          formData.down_payment_source_type,
          DLA_DOWN_PAYMENT_SOURCE_GIFT,
          downPaymentCtx,
          "Must provide Giftor Name",
        ),
        test(lessThanMaxLength(200), "Must not exceed 200 characters"),
      ],
      ...(includeSiteImprovement
        ? {
            site_improvement_cost: [
              test(notEmpty, "Must enter a value"),
              test(greaterThanMinAmount(0), "Must be greather than $0.00"),
            ],
          }
        : {}),
      ...(includeUpgrades
        ? {
            upgrades_cost: [
              test(notEmpty, "Must enter a value"),
              test(greaterThanMinAmount(0), "Must be greather than $0.00"),
            ],
          }
        : {}),
    };
  };
};

export default () => {
  const api = useWebClientApi();
  const [showUpgradeCost, setUpgradeCost] = useState(false);
  const [showImprovementCost, setImprovementCost] = useState(false);

  const validationMapper = getUpdatedValidationMapper({
    includeSiteImprovement: showImprovementCost,
    includeUpgrades: showUpgradeCost,
  });

  const sectionProgress = useSectionProgress();
  const { formData, updateField } = useFormLoad<HomeDownPaymentFormData>(
    {},
    () => api.getHome(),
    (data) => fromHomeDownPayment(data as DtoHome),
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: HomeDownPaymentFormData) =>
      api.updateHome(toUpdateHomeDownPaymentReq(data)),
    );
  const nextPagePath =
    sectionProgress["property"] && sectionProgress["demographics"]
      ? "../../progress"
      : "/overview";
  useResponseRedirect(nextPagePath, response);

  useEffect(() => {
    if (safeNumber(formData?.upgrades_cost)) {
      setUpgradeCost(true);
    }
    if (safeNumber(formData?.site_improvement_cost)) {
      setImprovementCost(true);
    }
  }, [formData?.site_improvement_cost, formData?.upgrades_cost]);

  const formErrors = validate<HomeDownPaymentFormData>(
    formData,
    validationMapper,
  );

  const homeValue = useMemo(
    () => toHomeDownPaymentFormData(formData ?? {}),
    [formData],
  );

  const visibleErrors = fieldErrorsAfterSubmit(
    homeDownPaymentErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    hasAttemptedFormSubmission,
  );

  return (
    <FormLayout
      title="What is the total home price?"
      subtitle="Include the price of the home, plus any costs associated with upgrades & additions."
      headerTitle="Home and Land"
      stepIndex={7}
      footerTitle={
        sectionProgress["property"] && sectionProgress["demographics"]
          ? "Save"
          : "Next"
      }
      clickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Home Price & Down Payment"
          headerIcon={
            <DollarSquareIcon
              variant="bulk-rounded"
              size={16}
              className="text-primary"
            />
          }
        >
          <FormTableBody>
            <HomeFields
              value={homeValue}
              onChange={(patch) => {
                const next = { ...homeValue, ...patch };
                if (patch.upgradesCost != null) {
                  setUpgradeCost(true);
                }
                if (patch.siteImprovementCost != null) {
                  setImprovementCost(true);
                }
                if ((next.downPaymentAmount ?? 0) <= 0) {
                  next.downPaymentSource = undefined;
                  next.downPaymentSourceOther = undefined;
                  next.downPaymentGiftorName = undefined;
                } else if (patch.downPaymentSource != null) {
                  if (
                    shouldClearOtherField(
                      patch.downPaymentSource,
                      DLA_DOWN_PAYMENT_SOURCE_OTHER,
                    )
                  ) {
                    next.downPaymentSourceOther = undefined;
                  }
                  if (
                    shouldClearOtherField(
                      patch.downPaymentSource,
                      DLA_DOWN_PAYMENT_SOURCE_GIFT,
                    )
                  ) {
                    next.downPaymentGiftorName = undefined;
                  }
                }
                const dlaPatch = fromHomeDownPaymentFormData(next);
                for (const [key, value] of Object.entries(dlaPatch)) {
                  updateField(key as keyof HomeDownPaymentFormData)(value);
                }
              }}
              errors={visibleErrors}
              showDownPaymentSection
            />
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
